import type {
  FollowUpTaskStatus,
  LoanOrderFacts,
  LoanFactCandidateFacts,
  LoginInput,
  MessageDirection,
  MessageType,
  Role,
  UserStatus,
  WhatsAppSessionStatus
} from "@scs/contracts";

export type PublicUser = {
  createdAt?: string;
  email: string;
  id: string;
  role: Role;
  sessionVersion?: number;
  status: UserStatus;
  tenantId: string;
  updatedAt?: string;
};

export type AuthSession = {
  expiresInSeconds: number;
  sessionToken: string;
  user: PublicUser;
};

export type SafeWhatsAppSession = {
  accountIdentityPending?: boolean;
  accountPhoneE164?: string | null;
  collectorId: string;
  channelOwner?: "LEGACY" | "CUTOVER_PENDING" | "GATEWAY";
  history: unknown;
  gatewayRuntime?: { pendingEventCount: number; reportedAt: string; state: "READY" | "STOPPED" | "STALE" | "AUTHENTICATING" | "DISCONNECTED" | "AUTH_FAILURE";
    lastReportedState?: "READY" | "STOPPED" | "AUTHENTICATING" | "DISCONNECTED" | "AUTH_FAILURE" };
  gatewayJobState?: string | null;
  gatewayId?: string | null;
  id: string;
  lastErrorCode: string | null;
  qr: { expiresAt: string; issuedAt: string; payload: string } | null;
  restartCount: number;
  sendAllowed: boolean;
  status: WhatsAppSessionStatus;
  tenantId: string;
  updatedAt: string;
};
export type GatewayPairingQr = { expiresAt: string; issuedAt: string; payload: string };
export type GatewayProvisioningRequest = {
  accountId: string;
  gatewayId: string | null;
  id: string;
  requestedBy: string;
  state: "PENDING" | "APPROVED" | "PROVISIONED" | "REJECTED" | "REVOKED";
  approvalInProgress: boolean;
  quotaRecoveryRequired?: boolean;
};
export type GatewayProvisioningCapability = {
  canRequest: boolean;
  canApprove: boolean;
  blockers: string[];
};

export type Customer = {
  assignedCollectorId: string;
  automationStatus: "ANALYSIS_ONLY" | "DISABLED";
  contactPolicy: "CONTACT_ALLOWED" | "DO_NOT_CONTACT";
  contactPreference: string | null;
  createdAt: string;
  id: string;
  language: string;
  name: string;
  phoneNumber: string;
  riskLevel: "HIGH" | "LOW" | "MEDIUM" | "UNKNOWN";
  tags: string[];
  tenantId: string;
  timeZone: string;
  updatedAt: string;
  whatsappLastSeenAt?: string | null;
  whatsappPresenceExpiresAt?: string | null;
  whatsappPresenceObservedAt?: string | null;
  whatsappPresenceStatus?: "LAST_SEEN" | "ONLINE" | "UNAVAILABLE";
};

export type CustomerPatch = Partial<
  Pick<
  Customer,
  | "automationStatus"
  | "contactPolicy"
  | "contactPreference"
  | "language"
  | "name"
  | "riskLevel"
  | "tags"
  | "timeZone"
  >
>;

export type CustomerAssignmentResult = {
  customer: Customer;
  history: CustomerAssignment[];
};

export type CustomerAssignment = {
  assignedBy: string;
  collectorId: string;
  customerId: string;
  endedAt: string | null;
  id: string;
  startedAt: string;
  tenantId: string;
};

export type GovernanceJob = {
  blockedReason: string | null;
  createdAt: string;
  expiresAt: string | null;
  id: string;
  request: { customerId?: string };
  requestedBy: string;
  result: unknown;
  status: "BLOCKED" | "FAILED" | "PENDING" | "RUNNING" | "SUCCEEDED";
  tenantId: string;
  type: "DELETE_CUSTOMER" | "EXPORT_TENANT";
  updatedAt: string;
};

export type ConversationSearchFilters = {
  collectorId?: string;
  customerName?: string;
  daysPastDueMax?: number;
  daysPastDueMin?: number;
  followUpDueFrom?: string;
  followUpDueTo?: string;
  intent?: string;
  loanNumber?: string;
  phoneNumber?: string;
  riskLevel?: Customer["riskLevel"];
  sessionStatus?: WhatsAppSessionStatus;
};

export type ConversationSearchResult = {
  conversationId: string | null;
  unreadCount?: number;
  customer: Customer;
  latestIntent: string | null;
  lastMessagePreview: string | null;
  latestSessionStatus: WhatsAppSessionStatus | null;
  lastMessageAt: string | null;
  matchingLoanNumbers: string[];
  nextFollowUpAt: string | null;
};

export type TimelineMessage = {
  attachmentMetadata?: MediaAttachment;
  direction: MessageDirection;
  id: string;
  occurredAt: string;
  origin: "AI_DRAFT" | "CUSTOMER" | "HUMAN";
  placeholder: string | null;
  safeMetadata: Record<string, boolean | null | number | string>;
  senderExternalId: string;
  sessionId: string;
  sessionStatus: WhatsAppSessionStatus;
  status: string;
  text: string | null;
  type: MessageType;
};

export type ConversationTimeline = {
  conversation: {
    createdAt: string;
    customerId: string;
    id: string;
    lastMessageAt: string | null;
    latestIntent: string | null;
    tenantId: string;
  };
  customer: Customer;
  messages: TimelineMessage[];
  outboundRequests: OutboundMessageRequest[];
  cursor?: string | null;
};

export type LoanOrder = LoanOrderFacts & {
  aiFactsEligible: boolean;
  createdAt: string;
  customerId: string;
  id: string;
  tenantId: string;
  updatedAt: string;
};

export type LoanFactCandidate = {
  confirmedValues: LoanFactCandidateFacts | null;
  conversationId: string;
  createdAt: string;
  customerId: string;
  facts: LoanFactCandidateFacts;
  id: string;
  modelVersion: string;
  reviewReason: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rulesVersion: string;
  sourceMessageId: string;
  sourceOrderVersion: string | null;
  status: "CONFIRMED" | "CONFLICT" | "FAILED" | "PENDING_REVIEW" | "REJECTED";
  tenantId: string;
  updatedAt: string;
};

export type SuggestionFeedbackAction =
  | { type: "COPIED" }
  | { editedText: string; type: "EDITED_AND_COPIED" }
  | { rating: "DOWN" | "UP"; reason?: string; type: "RATED" }
  | { reason: string; type: "ESCALATED" };

export type AiSuggestion = {
  confidence: number;
  conversationId: string;
  createdAt: string;
  customerId: string;
  feedbackEvents: Array<SuggestionFeedbackAction & { actorId: string; at: string }>;
  id: string;
  intent: string;
  isCurrent?: boolean;
  modelVersion: string;
  orderVersion?: string | null;
  originalText: string;
  policyVersion: string;
  rationale: string[];
  requiresApproval: boolean;
  risks: string[];
  tenantId: string;
  sourceMessageId?: string | null;
};

export type FollowUpTask = {
  assignedCollectorId: string;
  createdAt: string;
  customerId: string;
  dueAt: string;
  id: string;
  status: FollowUpTaskStatus;
  tenantId: string;
  title: string;
  updatedAt: string;
};

export type OutboundMessageRequest = {
  attachmentMetadata?: MediaAttachment;
  confirmedMessageId?: string | null;
  linkedMessageId?: string;
  attachmentId?: string | null;
  attachmentKind?: "IMAGE" | "VIDEO" | null;
  body: string;
  clientRequestId: string;
  conversationId: string;
  createdAt: string;
  externalMessageId: string | null;
  failureCode: string | null;
  id: string;
  status: "PENDING" | "SENDING" | "SENT" | "NEEDS_HUMAN_REVIEW" | "BLOCKED";
  updatedAt: string;
};

export type MediaAttachment = {
  displayName?: string;
  byteSize: number;
  contentType: "image/jpeg" | "image/png" | "image/webp" | "video/mp4";
  id: string;
  kind: "IMAGE" | "VIDEO";
};

type FetchImplementation = typeof fetch;

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details: unknown = null
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export class ApiClient {
  readonly #baseUrl: string;
  readonly #fetch: FetchImplementation;
  #sessionToken: string | null = null;
  #storedSession: AuthSession | null = null;
  #onUnauthorized: (() => void) | null = null;

  public constructor(
    baseUrl = "",
    fetchImplementation: FetchImplementation = (input, init) => fetch(input, init)
  ) {
    this.#baseUrl = baseUrl.replace(/\/$/u, "");
    this.#fetch = fetchImplementation;
    this.#storedSession = readStoredSession();
    this.#sessionToken = this.#storedSession?.sessionToken ?? null;
  }

  public getStoredSession(): AuthSession | null {
    return this.#storedSession;
  }

  public setSessionToken(sessionToken: string | null): void {
    this.#sessionToken = sessionToken;
  }

  public setUnauthorizedHandler(handler: (() => void) | null): void {
    this.#onUnauthorized = handler;
  }

  public async login(input: LoginInput): Promise<AuthSession> {
    const session = await this.#request<AuthSession>("/auth/login", {
      body: JSON.stringify({
        ...input,
        email: input.email.trim(),
        tenantId: input.tenantId.trim()
      }),
      method: "POST"
    });
    this.#sessionToken = session.sessionToken;
    this.#storedSession = session;
    writeStoredSession(session);
    return session;
  }

  public async logout(): Promise<void> {
    try {
      await this.#request<void>("/auth/logout", { method: "POST" });
    } finally {
      this.#sessionToken = null;
      this.#storedSession = null;
      clearStoredSession();
    }
  }

  public listUsers(): Promise<PublicUser[]> {
    return this.#request<PublicUser[]>("/users");
  }

  public createUser(input: {
    email: string;
    password?: string;
    role: Exclude<Role, "PLATFORM_ADMIN">;
  }): Promise<PublicUser> {
    return this.#request<PublicUser>("/users", {
      body: JSON.stringify(input),
      method: "POST"
    });
  }

  public updateUser(
    id: string,
    patch: { role?: Exclude<Role, "PLATFORM_ADMIN">; status?: UserStatus }
  ): Promise<PublicUser> {
    return this.#request<PublicUser>(`/users/${encodeURIComponent(id)}`, {
      body: JSON.stringify(patch),
      method: "PATCH"
    });
  }

  public deleteUser(id: string): Promise<void> {
    return this.#request<void>(`/users/${encodeURIComponent(id)}`, {
      method: "DELETE"
    });
  }

  public revokeUserSessions(id: string): Promise<void> {
    return this.#request<void>(
      `/admin/users/${encodeURIComponent(id)}/revoke-sessions`,
      { method: "POST" }
    );
  }

  public async listWhatsAppSessions(): Promise<SafeWhatsAppSession[]> {
    const body = await this.#request<unknown>("/whatsapp-sessions");
    if (!Array.isArray(body)) {
      throw new ApiError(502, "INVALID_SESSION_RESPONSE");
    }
    return body.map(toSafeWhatsAppSession);
  }

  public async bindWhatsAppSession(collectorId: string): Promise<SafeWhatsAppSession> {
    const body = await this.#request<unknown>("/whatsapp-sessions", {
      body: JSON.stringify({ collectorId }),
      method: "POST"
    });
    return toSafeWhatsAppSession(body);
  }

  public async getGatewayPairingQr(id: string): Promise<GatewayPairingQr | null> {
    const body = await this.#request<unknown>(`/whatsapp-sessions/${encodeURIComponent(id)}/gateway-pairing-qr`);
    if (!isRecord(body) || !(body["qr"] === null || (isRecord(body["qr"]) &&
        typeof body["qr"]["expiresAt"] === "string" && typeof body["qr"]["issuedAt"] === "string" &&
        typeof body["qr"]["payload"] === "string"))) {
      throw new ApiError(502, "INVALID_PAIRING_QR_RESPONSE");
    }
    return body["qr"] as GatewayPairingQr | null;
  }

  public archiveWhatsAppSession(id: string): Promise<void> {
    return this.#request<void>(`/whatsapp-sessions/${encodeURIComponent(id)}`, {
      method: "DELETE"
    });
  }

  public getGatewayProvisioningCapability(): Promise<GatewayProvisioningCapability> {
    return this.#request<GatewayProvisioningCapability>("/gateway-provisioning-capability");
  }

  public listGatewayProvisioningRequests(): Promise<GatewayProvisioningRequest[]> {
    return this.#request<GatewayProvisioningRequest[]>("/gateway-provisioning-requests");
  }

  public getMyGatewayProvisioningRequest(accountId: string): Promise<GatewayProvisioningRequest | null> {
    return this.#request<{ request: GatewayProvisioningRequest | null }>(
      `/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway-provisioning-request`
    ).then((body) => body.request);
  }

  public requestGatewayProvisioning(accountId: string): Promise<GatewayProvisioningRequest> {
    return this.#request<GatewayProvisioningRequest>(
      `/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway-provisioning-requests`,
      { method: "POST" }
    );
  }

  public decideGatewayProvisioning(id: string, decision: "APPROVE" | "REJECT"): Promise<GatewayProvisioningRequest> {
    return this.#request<GatewayProvisioningRequest>(
      `/gateway-provisioning-requests/${encodeURIComponent(id)}/decision`,
      { body: JSON.stringify({ decision }), method: "POST" }
    );
  }

  public cancelGatewayProvisioning(id: string): Promise<void> {
    return this.#request<void>(`/gateway-provisioning-requests/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  public executeGatewayProvisioning(accountId: string, requestId: string): Promise<{ gatewayId: string }> {
    return this.#request<{ gatewayId: string }>(`/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway`, {
      body: JSON.stringify({ requestId }), method: "POST"
    });
  }

  public prepareGatewayCutover(accountId: string, gatewayId: string): Promise<void> {
    return this.#request<void>(`/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway/prepare-cutover`, {
      body: JSON.stringify({ gatewayId }), method: "POST"
    });
  }

  public finishGatewayCutover(accountId: string, gatewayId: string): Promise<void> {
    return this.#request<void>(`/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway/cutover`, {
      body: JSON.stringify({ gatewayId }), method: "POST"
    });
  }

  public requestGatewayRecovery(accountId: string): Promise<{ status: "QUEUED" | "ALREADY_PENDING" }> {
    return this.#request<{ status: "QUEUED" | "ALREADY_PENDING" }>(
      `/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway/recover`, { method: "POST" }
    );
  }

  public requestGatewayPause(accountId: string): Promise<{ status: "QUEUED" | "ALREADY_PENDING" }> {
    return this.#request<{ status: "QUEUED" | "ALREADY_PENDING" }>(
      `/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway/pause`, { method: "POST" }
    );
  }

  public requestGatewayResume(accountId: string): Promise<{ status: "QUEUED" | "ALREADY_PENDING" }> {
    return this.#request<{ status: "QUEUED" | "ALREADY_PENDING" }>(
      `/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway/resume`, { method: "POST" }
    );
  }

  public returnGatewayToLegacy(accountId: string, gatewayId: string): Promise<void> {
    return this.#request<void>(`/whatsapp-sessions/${encodeURIComponent(accountId)}/gateway/return-to-legacy`, {
      body: JSON.stringify({ gatewayId }), method: "POST"
    });
  }

  public searchConversations(
    filters: ConversationSearchFilters = {}
  ): Promise<ConversationSearchResult[]> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    const suffix = query.size === 0 ? "" : `?${query.toString()}`;
    return this.#request<ConversationSearchResult[]>(`/search/conversations${suffix}`);
  }

  public getConversationTimeline(id: string, cursor?: string | null): Promise<ConversationTimeline> {
    const query = cursor === undefined || cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`;
    return this.#request<ConversationTimeline>(
      `/conversations/${encodeURIComponent(id)}/timeline${query}`
    );
  }

  public async coreAvailable(): Promise<boolean> {
    try {
      const response = await this.#fetch(`${this.#baseUrl}/ready`, { cache: "no-store" });
      return response.ok;
    } catch { return false; }
  }

  public listOrders(customerId: string): Promise<LoanOrder[]> {
    return this.#request<LoanOrder[]>(
      `/customers/${encodeURIComponent(customerId)}/orders`
    );
  }

  public listLoanFactCandidates(conversationId: string): Promise<LoanFactCandidate[]> {
    return this.#request<LoanFactCandidate[]>(
      `/conversations/${encodeURIComponent(conversationId)}/loan-fact-candidates`
    );
  }

  public reviewLoanFactCandidate(input: {
    action: "CONFIRM" | "REJECT";
    candidateId: string;
    expectedUpdatedAt: string;
    reason?: string;
  }): Promise<LoanFactCandidate> {
    return this.#request<LoanFactCandidate>(
      `/loan-fact-candidates/${encodeURIComponent(input.candidateId)}/review`,
      {
        body: JSON.stringify({
          action: input.action,
          expectedUpdatedAt: input.expectedUpdatedAt,
          ...(input.action === "REJECT" ? { reason: input.reason } : {})
        }),
        method: "POST"
      }
    );
  }

  public createOrder(customerId: string, facts: LoanOrderFacts): Promise<LoanOrder> {
    return this.#request<LoanOrder>(
      `/customers/${encodeURIComponent(customerId)}/orders`,
      { body: JSON.stringify(facts), method: "POST" }
    );
  }

  public updateOrder(id: string, facts: LoanOrderFacts): Promise<LoanOrder> {
    return this.#request<LoanOrder>(
      `/orders/${encodeURIComponent(id)}`,
      { body: JSON.stringify(facts), method: "PATCH" }
    );
  }

  public updateCustomer(id: string, patch: CustomerPatch): Promise<Customer> {
    return this.#request<Customer>(
      `/customers/${encodeURIComponent(id)}`,
      { body: JSON.stringify(patch), method: "PATCH" }
    );
  }

  public reassignCustomer(
    customerId: string,
    collectorId: string
  ): Promise<CustomerAssignmentResult> {
    return this.#request<CustomerAssignmentResult>(
      `/customers/${encodeURIComponent(customerId)}/assignment`,
      { body: JSON.stringify({ collectorId }), method: "POST" }
    );
  }

  public listCustomerAssignments(customerId: string): Promise<CustomerAssignment[]> {
    return this.#request<CustomerAssignment[]>(
      `/customers/${encodeURIComponent(customerId)}/assignments`
    );
  }

  public listGovernanceJobs(): Promise<GovernanceJob[]> {
    return this.#request<GovernanceJob[]>("/governance/jobs");
  }

  public requestGovernanceJob(
    input: { type: "EXPORT_TENANT" } | { customerId: string; type: "DELETE_CUSTOMER" }
  ): Promise<GovernanceJob> {
    return this.#request<GovernanceJob>("/governance/jobs", {
      body: JSON.stringify(input),
      method: "POST"
    });
  }

  public listSuggestions(conversationId: string): Promise<AiSuggestion[]> {
    return this.#request<AiSuggestion[]>(
      `/conversations/${encodeURIComponent(conversationId)}/ai`
    );
  }

  public requestOutboundMessage(
    conversationId: string,
    input: { attachmentId?: string; attachmentKind?: "IMAGE" | "VIDEO"; clientRequestId: string; text?: string }
  ): Promise<OutboundMessageRequest> {
    return this.#request<OutboundMessageRequest>(
      `/conversations/${encodeURIComponent(conversationId)}/outbound-messages`,
      { body: JSON.stringify(input), method: "POST" }
    );
  }

  public async uploadMedia(file: File): Promise<MediaAttachment> {
    const response = await this.#fetch(`${this.#baseUrl}/media-attachments`, {
      body: file,
      headers: {
        ...(this.#sessionToken === null ? {} : { authorization: `Bearer ${this.#sessionToken}` }),
        "content-type": file.type
      },
      method: "POST"
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const code = isRecord(body) && typeof body["error"] === "string" ? body["error"] : "REQUEST_FAILED";
      if (response.status === 401) this.#clearUnauthorizedSession();
      throw new ApiError(response.status, code, body);
    }
    if (!isRecord(body) || typeof body["id"] !== "string" || typeof body["byteSize"] !== "number" ||
      (body["kind"] !== "IMAGE" && body["kind"] !== "VIDEO") ||
      (body["contentType"] !== "image/jpeg" && body["contentType"] !== "image/png" && body["contentType"] !== "image/webp" && body["contentType"] !== "video/mp4")) {
      throw new ApiError(502, "INVALID_MEDIA_ATTACHMENT_RESPONSE");
    }
    return { byteSize: body["byteSize"], contentType: body["contentType"], id: body["id"], kind: body["kind"] };
  }

  public getOutboundMessageRequest(id: string): Promise<OutboundMessageRequest> {
    return this.#request<OutboundMessageRequest>(
      `/outbound-messages/${encodeURIComponent(id)}`
    );
  }

  public markConversationRead(conversationId: string): Promise<void> {
    return this.#request<void>(`/conversations/${encodeURIComponent(conversationId)}/read`, { method: "POST" });
  }

  public async previewMedia(conversationId: string, attachmentId: string): Promise<Blob> {
    const response = await this.#fetch(`${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/media-attachments/${encodeURIComponent(attachmentId)}`, {
      headers: { ...(this.#sessionToken === null ? {} : { authorization: `Bearer ${this.#sessionToken}` }) }
    });
    if (!response.ok) throw new ApiError(response.status, "MEDIA_PREVIEW_FAILED");
    return await response.blob();
  }

  public async previewInboundMedia(conversationId: string, messageId: string): Promise<Blob> {
    const response = await this.#fetch(`${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/media`, {
      headers: { ...(this.#sessionToken === null ? {} : { authorization: `Bearer ${this.#sessionToken}` }) }
    });
    if (!response.ok) throw new ApiError(response.status, "MEDIA_PREVIEW_FAILED");
    return await response.blob();
  }

  public recordSuggestionAction(
    suggestionId: string,
    action: SuggestionFeedbackAction
  ): Promise<AiSuggestion> {
    return this.#request<AiSuggestion>(
      `/suggestions/${encodeURIComponent(suggestionId)}/actions`,
      { body: JSON.stringify(action), method: "POST" }
    );
  }

  public listFollowUpTasks(): Promise<FollowUpTask[]> {
    return this.#request<FollowUpTask[]>("/follow-up-tasks");
  }

  public createFollowUpTask(
    customerId: string,
    input: { dueAt: string; title: string }
  ): Promise<FollowUpTask> {
    return this.#request<FollowUpTask>(
      `/customers/${encodeURIComponent(customerId)}/follow-up-tasks`,
      { body: JSON.stringify(input), method: "POST" }
    );
  }

  public updateFollowUpTask(
    id: string,
    patch: { dueAt?: string; status?: FollowUpTaskStatus; title?: string }
  ): Promise<FollowUpTask> {
    return this.#request<FollowUpTask>(
      `/follow-up-tasks/${encodeURIComponent(id)}`,
      { body: JSON.stringify(patch), method: "PATCH" }
    );
  }

  async #request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (init.body !== undefined) headers.set("content-type", "application/json");
    if (this.#sessionToken !== null) {
      headers.set("authorization", `Bearer ${this.#sessionToken}`);
    }
    const response = await this.#fetch(`${this.#baseUrl}${path}`, {
      ...init,
      headers
    });
    if (!response.ok) {
      const body = await readJson(response);
      const code = isRecord(body) && typeof body["error"] === "string"
        ? body["error"]
        : "REQUEST_FAILED";
      const error = new ApiError(response.status, code, body);
      if (response.status === 401) {
        this.#clearUnauthorizedSession();
      }
      throw error;
    }
    if (response.status === 204) return undefined as T;
    return await response.json() as T;
  }

  #clearUnauthorizedSession(): void {
    this.#sessionToken = null;
    this.#storedSession = null;
    clearStoredSession();
    this.#onUnauthorized?.();
  }
}

function readStoredSession(): AuthSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(sessionStorageKey) ?? window.localStorage.getItem("scs.auth.session");
    if (raw === null) return null;
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || typeof value["expiresAt"] !== "number" ||
        value["expiresAt"] <= Date.now() || typeof value["sessionToken"] !== "string" ||
        typeof value["expiresInSeconds"] !== "number" || !isRecord(value["user"])) {
      clearStoredSession();
      return null;
    }
    window.localStorage.setItem(sessionStorageKey, raw);
    window.localStorage.removeItem("scs.auth.session");
    return value as AuthSession;
  } catch {
    return null;
  }
}

function writeStoredSession(session: AuthSession): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(sessionStorageKey, JSON.stringify({
      ...session,
      expiresAt: Date.now() + session.expiresInSeconds * 1_000
    }));
  } catch { /* storage may be disabled */ }
}

function clearStoredSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(sessionStorageKey);
    window.localStorage.removeItem("scs.auth.session");
  } catch { /* storage may be disabled */ }
}

const sessionStorageKey = "scs.auth.session.v1";

const sensitiveSessionKey = /credential|secret|token|envelope|ciphertext|private.?key|api.?key/iu;

function toSafeWhatsAppSession(value: unknown): SafeWhatsAppSession {
  if (!isRecord(value) || containsSensitiveKey(value)) {
    throw new ApiError(502, "UNSAFE_SESSION_RESPONSE");
  }
  const qrValue = value["qr"];
  const qr = qrValue === null
    ? null
    : isRecord(qrValue) &&
        typeof qrValue["expiresAt"] === "string" &&
        typeof qrValue["issuedAt"] === "string" &&
        typeof qrValue["payload"] === "string"
      ? {
          expiresAt: qrValue["expiresAt"],
          issuedAt: qrValue["issuedAt"],
          payload: qrValue["payload"]
        }
      : undefined;
  const runtimeValue = value["gatewayRuntime"];
  const lastReportedValue = isRecord(runtimeValue) ? runtimeValue["lastReportedState"] : undefined;
  const lastReportedState: "READY" | "STOPPED" | "AUTHENTICATING" | "DISCONNECTED" | "AUTH_FAILURE" | undefined =
    lastReportedValue === "READY" || lastReportedValue === "STOPPED" || lastReportedValue === "AUTHENTICATING" ||
    lastReportedValue === "DISCONNECTED" || lastReportedValue === "AUTH_FAILURE" ? lastReportedValue : undefined;
  const gatewayRuntime: SafeWhatsAppSession["gatewayRuntime"] | null = runtimeValue === undefined ? undefined : isRecord(runtimeValue) &&
    typeof runtimeValue["pendingEventCount"] === "number" && typeof runtimeValue["reportedAt"] === "string" &&
    (lastReportedValue === undefined || lastReportedState !== undefined) &&
    (runtimeValue["state"] === "READY" || runtimeValue["state"] === "STOPPED" || runtimeValue["state"] === "STALE" ||
      runtimeValue["state"] === "AUTHENTICATING" || runtimeValue["state"] === "DISCONNECTED" || runtimeValue["state"] === "AUTH_FAILURE")
    ? { pendingEventCount: runtimeValue["pendingEventCount"], reportedAt: runtimeValue["reportedAt"], state: runtimeValue["state"],
        ...(lastReportedState === undefined ? {} : { lastReportedState }) }
    : null;
  if (
    typeof value["collectorId"] !== "string" ||
    !(value["channelOwner"] === undefined || value["channelOwner"] === "LEGACY" || value["channelOwner"] === "CUTOVER_PENDING" || value["channelOwner"] === "GATEWAY") ||
    !(value["accountPhoneE164"] === undefined || value["accountPhoneE164"] === null || typeof value["accountPhoneE164"] === "string") ||
    !(value["accountIdentityPending"] === undefined || typeof value["accountIdentityPending"] === "boolean") ||
    !(value["gatewayJobState"] === undefined || value["gatewayJobState"] === null || typeof value["gatewayJobState"] === "string") ||
    !(value["gatewayId"] === undefined || value["gatewayId"] === null || typeof value["gatewayId"] === "string") ||
    typeof value["id"] !== "string" ||
    !(value["lastErrorCode"] === null || typeof value["lastErrorCode"] === "string") ||
    qr === undefined ||
    gatewayRuntime === null ||
    typeof value["restartCount"] !== "number" ||
    typeof value["sendAllowed"] !== "boolean" ||
    typeof value["status"] !== "string" ||
    typeof value["tenantId"] !== "string" ||
    typeof value["updatedAt"] !== "string"
  ) {
    throw new ApiError(502, "INVALID_SESSION_RESPONSE");
  }
  return {
    ...(value["accountIdentityPending"] === undefined ? {} : { accountIdentityPending: value["accountIdentityPending"] }),
    ...(value["accountPhoneE164"] === undefined ? {} : { accountPhoneE164: value["accountPhoneE164"] }),
    collectorId: value["collectorId"],
    ...(value["channelOwner"] === undefined ? {} : { channelOwner: value["channelOwner"] }),
    history: value["history"],
    ...(gatewayRuntime === undefined ? {} : { gatewayRuntime }),
    ...(value["gatewayJobState"] === undefined ? {} : { gatewayJobState: value["gatewayJobState"] }),
    ...(value["gatewayId"] === undefined ? {} : { gatewayId: value["gatewayId"] }),
    id: value["id"],
    lastErrorCode: value["lastErrorCode"],
    qr,
    restartCount: value["restartCount"],
    sendAllowed: value["sendAllowed"],
    status: value["status"] as WhatsAppSessionStatus,
    tenantId: value["tenantId"],
    updatedAt: value["updatedAt"]
  };
}

function containsSensitiveKey(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(containsSensitiveKey);
  if (!isRecord(value)) return false;
  return Object.entries(value).some(
    ([key, nested]) => sensitiveSessionKey.test(key) || containsSensitiveKey(nested)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
